/**
 * Show success notification
 * @param {string} message - Success message to display
 */
export const showSuccess = (message) => {
  // For now use console.log as a fallback until we have a proper toast system
  console.log('✅ Success:', message);
  
  // You can replace this with actual toast library implementation
  // toast.success(message);
};

/**
 * Show error notification
 * @param {string} message - Error message to display
 * @param {Error} error - Optional error object for debugging
 */
export const showError = (message, error = null) => {
  console.error('❌ Error:', message, error);
  
  // You can replace this with actual toast library implementation
  // toast.error(message);
};

/**
 * Show warning notification
 * @param {string} message - Warning message to display
 */
export const showWarning = (message) => {
  console.warn('⚠️ Warning:', message);
  
  // You can replace this with actual toast library implementation
  // toast.warning(message);
};

/**
 * Show info notification
 * @param {string} message - Info message to display
 */
export const showInfo = (message) => {
  console.info('ℹ️ Info:', message);
  
  // You can replace this with actual toast library implementation
  // toast.info(message);
};

/**
 * Handle API errors with proper user feedback
 * @param {Error} error - The error object
 * @param {string} fallbackMessage - Fallback message if no specific error message
 */
export const handleApiError = (error, fallbackMessage = 'An unexpected error occurred') => {
  let userMessage = fallbackMessage;
  
  if (error?.response?.data?.message) {
    // API returned a specific error message
    userMessage = error.response.data.message;
  } else if (error?.response?.status) {
    // Handle common HTTP status codes
    switch (error.response.status) {
      case 401:
        userMessage = 'Authentication failed. Please log in again.';
        break;
      case 403:
        userMessage = 'You do not have permission to perform this action.';
        break;
      case 404:
        userMessage = 'The requested resource was not found.';
        break;
      case 422:
        userMessage = 'Invalid data provided. Please check your input.';
        break;
      case 500:
        userMessage = 'Server error. Please try again later.';
        break;
      case 503:
        userMessage = 'Service temporarily unavailable. Please try again later.';
        break;
      default:
        userMessage = `Request failed (${error.response.status}). Please try again.`;
    }
  } else if (error?.message) {
    // Network error or other error with message
    if (error.message.includes('Network Error')) {
      userMessage = 'Network connection error. Please check your internet connection.';
    } else {
      userMessage = error.message;
    }
  }
  
  showError(userMessage, error);
  return userMessage;
};

/**
 * Create error handler function with custom message
 * @param {string} context - Context of the operation (e.g., "loading dashboard data")
 * @returns {Function} Error handler function
 */
export const createErrorHandler = (context) => {
  return (error) => {
    const message = `Failed to ${context}`;
    return handleApiError(error, message);
  };
};

/**
 * Show loading state feedback (could be extended with actual loading indicators)
 * @param {string} message - Loading message
 */
export const showLoading = (message) => {
  console.log('🔄 Loading:', message);
  // This could be extended to show actual loading spinners/indicators
};

/**
 * Hide loading state
 */
export const hideLoading = () => {
  console.log('✓ Loading complete');
  // This could be extended to hide loading spinners/indicators
};

const notificationUtils = {
  showSuccess,
  showError,
  showWarning,
  showInfo,
  handleApiError,
  createErrorHandler,
  showLoading,
  hideLoading
};

export default notificationUtils;
